import { observer } from 'mobx-react';
import * as React from 'react';
import cn from 'classnames';

import Typography from 'shared/newComponents/Typography';
import { Button } from 'shared/newComponents/buttons';
import {
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { ComponentType } from 'shared/entities/components/Component';
import { urls } from 'shared/entities/domain';
import { DateFormat, transformDate } from 'shared/entities/date';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import { ButtonColor, ButtonSize } from 'shared/newEntities/components/Button';
import { useUiStore } from 'stores';

import './Balance.modules.scss';

export type BalanceProps = (
  | {
      state: 'trial';
      daysLeft: number;
      paid: boolean;
      isElamaUser: boolean;
      isCorporate: boolean;
    }
  | {
      state: 'suspended';
      balance: number;
      daysLeft: number;
    }
  | {
      state: 'active';
      date: Date;
    }
  | {
      state: 'inactive';
    }
) & {
  className?: string;
};

const Balance: React.FC<BalanceProps> = (props) => {
  const uiStore = useUiStore();

  const suspendedState =
    props.state !== 'suspended'
      ? null
      : props.daysLeft >= 10
        ? 'info'
        : props.daysLeft >= 5
          ? 'warning'
          : 'error';

  const isSpecialUser =
    props.state === 'trial' && (props.isElamaUser || props.isCorporate);

  return (
    <div
      styleName={cn(
        'balance',
        props.state !== 'suspended' && `balance_${props.state}`,
        suspendedState && `balance_suspended-${suspendedState}`
      )}
      className={props.className}
    >
      <div>
        <Typography
          styleName="balance__title"
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontWeight={TypographyWeight.semiBold}
          fontColor={null}
        >
          {props.state === 'suspended' ? (
            <TypedTransComponent
              i18nKey="Balance.suspended.title"
              ns="newComponents"
            >
              На балансе{' '}
              <span styleName="balance__sum">{{ balance: props.balance }}</span>{' '}
              ₽
            </TypedTransComponent>
          ) : isSpecialUser ? (
            (t) =>
              t('Balance.trial.titleForSpecialUser', {
                ns: 'newComponents'
              })
          ) : (
            (t) =>
              t(`Balance.${props.state}.title`, {
                ns: 'newComponents'
              })
          )}
        </Typography>
        <Typography
          styleName="balance__description"
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={null}
        >
          {props.state === 'trial' || props.state === 'suspended'
            ? (t) =>
                t(`Balance.${props.state}.description`, {
                  ns: 'newComponents',
                  count: props.daysLeft
                })
            : props.state === 'active'
              ? (t) =>
                  t('Balance.active.description', {
                    ns: 'newComponents',
                    date: transformDate(props.date, DateFormat.dateMonth)
                  })
              : (t) =>
                  t('Balance.inactive.description', {
                    ns: 'newComponents'
                  })}
        </Typography>
      </div>
      {props.state === 'trial' && props.isElamaUser ? null : (
        <Button
          type={ComponentType.link}
          to={urls.SETTINGS.CABINET.BALANCE.create()}
          color={ButtonColor.gray}
          styleName="balance__button"
          size={ButtonSize.sm}
          onClick={uiStore.isMobileSize ? uiStore.menuState.close : undefined}
          transparentBorder={
            props.state === 'active' ||
            props.state === 'trial' ||
            props.state === 'inactive'
          }
        >
          {(t) =>
            t(
              `Balance.${
                props.state === 'trial' && (props.paid || props.isCorporate)
                  ? 'active'
                  : props.state
              }.button`,
              {
                ns: 'newComponents'
              }
            )
          }
        </Button>
      )}
    </div>
  );
};

export default observer(Balance);
